// _public_pc/PCLayout.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { FaRunning, FaChevronUp, FaChevronDown, FaRoad, FaClock } from 'react-icons/fa';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaInfoCircle, 
  FaChevronLeft, 
  FaChevronRight,
  FaLock
} from 'react-icons/fa';
import { LuMedal, LuCheck, LuClock, LuTrendingUp, LuCamera } from "react-icons/lu";
import { FaShoppingBag, FaUsers, FaFlag, FaFlagCheckered, FaTrophy } from 'react-icons/fa';
import api from '../../../api/axios';
import NavbarPC from './NavbarPC';
import { useBanner } from '../_shared/hooks/useBanner';
import { useColors } from '../_shared/hooks/useColors';
import { useInfo } from '../_shared/hooks/useInfo';
import { useRoutes } from '../_shared/hooks/useRoutes';
import { useCompetitors } from '../_shared/hooks/useCompetitors';
import LiveResults from './LiveResults';
import { usePackages } from '../_shared/hooks/usePackages';
import { useSponsors } from '../_shared/hooks/useSponsors';
import { useGallery } from '../_shared/hooks/useGallery';
import PCFormulario from '../../_forms/_public_pc/PCFormulario';
import { useEventData } from '../_shared/hooks/useEventCountdown';
import EventCountdown from '../_shared/utils/EventCountdown';
import './PCLayout.css';

// ==========================================
// 1. MARCADORES ESTILO GARMIN (Círculos limpios)
// ==========================================
const createGarminMarker = (label, type) => {
  const startColor = '#bc00dd'; // Morado/Rosado
  const endColor = '#00c6fb';   // Celeste
  
  const color = type === 'start' ? startColor : endColor;
  const dotColor = type === 'start' ? startColor : endColor;
  
  return L.divIcon({
    html: `
      <div class="pc-garmin-marker">
        <div class="pc-garmin-dot" style="background-color: ${dotColor};"></div>
        <div class="pc-garmin-label" style="color: ${color};">● ${label}</div>
      </div>
    `,
    className: 'pc-leaflet-garmin',
    iconSize: [80, 24],
    iconAnchor: [12, 12],
    popupAnchor: [23, -10]
  });
};

// ==========================================
// 2. MAP CONTROLLER CON RESPONSIVE PADDING
// ==========================================
function MapController({ path }) {
  const map = useMap();

  useEffect(() => {
    if (path && path.length > 1) {
      const isMobile = window.innerWidth < 768;
      const paddingValue = isMobile ? 30 : 80;

      map.flyToBounds(path, {
        padding: [paddingValue, paddingValue],
        animate: true,
        duration: 1.2
      });
    } else if (path && path.length === 1) {
      map.flyTo(path[0], 15, { animate: true, duration: 1.2 });
    }
  }, [map, path]);

  return null;
}

// ==========================================
// 3. RUTA CON DEGRADADO Y ANIMACIÓN (UNIFICADO)
// ==========================================
function AnimatedGradientPolyline({ positions, colors, weight }) {
  const [dashOffset, setDashOffset] = useState(1000);

  useEffect(() => {
    setDashOffset(1000);
    const timer = setTimeout(() => setDashOffset(0), 50);
    return () => clearTimeout(timer);
  }, [positions]);

  // Si solo hay 2 puntos, dibujar una sola línea con el primer color
  if (positions.length === 2) {
    return (
      <Polyline
        positions={positions}
        pathOptions={{
          color: colors[0],
          weight: weight,
          opacity: 0.9,
          dashArray: '1000',
          dashOffset: dashOffset.toString(),
          className: 'pc-animated-path-transition'
        }}
      />
    );
  }

  // Si hay más de 2 puntos, dibujar segmentos con degradado
  const segments = [];
  const steps = positions.length - 1;

  for (let i = 0; i < steps; i++) {
    const ratio = i / steps;
    const r = Math.round(parseInt(colors[0].slice(1, 3), 16) * (1 - ratio) + parseInt(colors[1].slice(1, 3), 16) * ratio);
    const g = Math.round(parseInt(colors[0].slice(3, 5), 16) * (1 - ratio) + parseInt(colors[1].slice(3, 5), 16) * ratio);
    const b = Math.round(parseInt(colors[0].slice(5, 7), 16) * (1 - ratio) + parseInt(colors[1].slice(5, 7), 16) * ratio);
    
    const segmentColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

    segments.push(
      <Polyline 
        key={`seg-${i}`} 
        positions={[positions[i], positions[i + 1]]} 
        pathOptions={{ 
          color: segmentColor, 
          weight: weight, 
          opacity: 0.9,
          dashArray: '1000',
          dashOffset: dashOffset.toString(),
          className: 'pc-animated-path-transition'
        }}
      />
    );
  }

  return <>{segments}</>;
}

// ==========================================
// 4. COMPONENTE PRINCIPAL
// ==========================================
const RoutesInteractiveContainer = ({ routes, colors }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeRoute = routes[activeIdx] || routes[0];

  const gradients = useMemo(() => [
    ["#bc00dd", "#00c6fb"],
    ["#ff416c", "#ffb347"],
    ["#00c853", "#64dd17"],
    ["#ff9800", "#ffd54f"],
    ["#3f51b5", "#00bcd4"],
    ["#e91e63", "#9c27b0"],
    ["#00acc1", "#26c6da"],
    ["#7c4dff", "#18ffff"]
  ], []);

  const currentGradient = gradients[activeIdx % gradients.length];

  // ==========================================
  // 5. MAP DATA CON useMemo
  // ==========================================
  const mapData = useMemo(() => {
    const defaultData = {
      start: [-16.5000, -68.1500],
      end: [-16.5000, -68.1500],
      path: [[-16.5000, -68.1500]],
      elevation_gain: null,
      elevation_loss: null,
      terrain: null,
      est_time: null
    };

    if (!activeRoute?.map_data) return defaultData;

    try {
      const parsed = typeof activeRoute.map_data === "string"
        ? JSON.parse(activeRoute.map_data)
        : activeRoute.map_data;

      return {
        start: parsed.start || defaultData.start,
        end: parsed.end || defaultData.end,
        path: parsed.path || [parsed.start || defaultData.start],
        elevation_gain: parsed.elevation_gain || null,
        elevation_loss: parsed.elevation_loss || null,
        terrain: parsed.terrain || null,
        est_time: parsed.est_time || null
      };
    } catch {
      return defaultData;
    }
  }, [activeRoute]);

  // ==========================================
  // 6. SEGMENTOS DE RUTA CON useMemo (OPTIMIZADO)
  // ==========================================
  const segmentedPolylines = useMemo(() => {
    const points = mapData.path;
    if (points.length < 2) return null;

    return (
      <AnimatedGradientPolyline 
        positions={points} 
        colors={currentGradient} 
        weight={6}
      />
    );
  }, [mapData.path, currentGradient]);

  const isCircularCircuit = mapData.start[0] === mapData.end[0] && mapData.start[1] === mapData.end[1];

  const getDistanceBadge = (title) => {
    return title || "Ruta";
  };

  return (
    <div className="pc-routes-split-screen">
      {/* DEFS SVG PARA ICONOS */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          {gradients.map(([start, end], i) => (
            <linearGradient key={i} id={`icon-gradient-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={start} />
              <stop offset="100%" stopColor={end} />
            </linearGradient>
          ))}
        </defs>
      </svg>

      {/* CONTENEDOR IZQUIERDO: MAPA */}
      <div className="pc-route-left-panel-bg" style={{ position: 'relative', height: '100%', transition: 'all 0.35s ease', pointerEvents: 'auto' }}>
        <MapContainer 
          center={mapData.start} 
          zoom={14} 
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 0 }}
          zoomControl={true}
          scrollWheelZoom={true}
          dragging={true}
          touchZoom={true}
          doubleClickZoom={true}
        >
          <TileLayer
    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* ✅ SOLO UNA RUTA - La optimizada con animación */}
          {segmentedPolylines}

          {/* Marcadores Estilo Garmin */}
          {isCircularCircuit ? (
            <Marker position={mapData.start} icon={createGarminMarker('START / FINISH', 'start')}>
              <Popup><b>Circuito Cerrado:</b> Partida y Meta en el mismo punto.</Popup>
            </Marker>
          ) : (
            <>
              <Marker position={mapData.start} icon={createGarminMarker('START', 'start')}>
                <Popup><b>Punto de Partida Oficial</b></Popup>
              </Marker>
              <Marker position={mapData.end} icon={createGarminMarker('FINISH', 'end')}>
                <Popup><b>Línea de Meta</b></Popup>
              </Marker>
            </>
          )}

          <MapController path={mapData.path} />
        </MapContainer>

        {/* OVERLAY */}
        <div 
          style={{ 
            position: 'absolute',
            top: 10,
            left: 50,
            zIndex: 10,  // ← Por encima del mapa
            pointerEvents: 'none' // ← No bloquea el mapa
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            <div className="pc-panel-header-titles">
              <span className="pc-panel-subtitle" style={{ color: currentGradient[0] }}>
                CIRCUITO INTERACTIVO
              </span>
              <h2 className="pc-panel-main-title">
                {getDistanceBadge(activeRoute?.title)}
              </h2>
            </div>
            
            
              {activeRoute?.link && (

                  <a 
                    href={activeRoute.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="pc-split-strava-btn"
                  >
                    <span>Ver en Strava</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l3.836 7.529h5.051L10.37.072 2.304 15.873h5.05" />
                    </svg>
                  </a>
                
              )}
            
          </div>
        </div>

        {/* ⭐ TARJETA FLOTANTE CON TRANSICIÓN */}
        {(mapData.elevation_gain || mapData.elevation_loss || mapData.terrain || mapData.est_time) && (
          <div className="pc-map-floating-stats-card" style={{         position: 'absolute',
          bottom: 30,
          left: 30,
          zIndex: 10,
          pointerEvents: 'none',
  transition: 'all 0.35s ease' }}>
            <div className="pc-stat-item">
              <span className="pc-stat-title">Distancia</span>
              <div className="pc-stat-value-wrapper">
                <FaRunning style={{ color: currentGradient[0] }} />
                <span className="pc-stat-value">{getDistanceBadge(activeRoute?.title)}</span>
              </div>
            </div>
            
            {/* ✅ Desnivel + */}
            {mapData.elevation_gain && (
              <div className="pc-stat-item">
                <span className="pc-stat-title">Desnivel +</span>
                <div className="pc-stat-value-wrapper">
                  <FaChevronUp style={{ color: '#00E676' }} />
                  <span className="pc-stat-value">+{mapData.elevation_gain}m</span>
                </div>
              </div>
            )}
            
            {/* ✅ Desnivel - */}
            {mapData.elevation_loss && (
              <div className="pc-stat-item">
                <span className="pc-stat-title">Desnivel -</span>
                <div className="pc-stat-value-wrapper">
                  <FaChevronDown style={{ color: '#FF1744' }} />
                  <span className="pc-stat-value">-{mapData.elevation_loss}m</span>
                </div>
              </div>
            )}
            
            {mapData.terrain && (
              <div className="pc-stat-item">
                <span className="pc-stat-title">Superficie</span>
                <div className="pc-stat-value-wrapper">
                  <FaRoad style={{ color: '#00B5FF' }} />
                  <span className="pc-stat-value">{mapData.terrain}</span>
                </div>
              </div>
            )}
            
            {mapData.est_time && (
              <div className="pc-stat-item">
                <span className="pc-stat-title">Tiempo Est.</span>
                <div className="pc-stat-value-wrapper">
                  <FaClock style={{ color: '#FFD54F' }} />
                  <span className="pc-stat-value">{mapData.est_time}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CONTENEDOR DERECHO */}
      <div className="pc-route-right-panel">
        <div className="pc-right-panel-content">
          <div className="pc-panel-header-titles">
            <span className="pc-panel-subtitle-light">DISTANCIAS</span>
            <h2 className="pc-panel-main-title-light">Elige tu Distancia</h2>
          </div>

          <div className="pc-split-selectors-vertical-stack">
            {routes.map((route, idx) => {
              const isActive = idx === activeIdx;
              const distance = getDistanceBadge(route.title);
              const cleanTitle = route.title?.replace(/(\d+\s*K|\d+\s*k|-)/g, '').trim() || "Circuito Oficial";
              const gradient = gradients[idx % gradients.length];
              const gradientId = `icon-gradient-${idx % gradients.length}`;
              return (
                <div
                  key={idx}
                  className={`pc-split-selector-card-row ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveIdx(idx)}
                  style={{
                    borderLeftColor: isActive ? (colors?.primaryColor || '#bc00dd') : 'transparent'
                  }}
                >
                  <div className="pc-split-card-row-header">
                    <span className="pc-split-card-dist-title" style={{
                      "--route-gradient": `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
                      transform: isActive ? "translateY(-10px) scale(1.06)" : "scale(1)",
                      borderLeftColor: "transparent"
                    }}>
                      {distance}
                    </span>
                  </div>
                  <div className="pc-split-card-icon-circle">
                    <FaRunning className="pc-split-card-icon" style={{
                      fill: `url(#${gradientId})`
                    }}/>
                  </div>
                  <div className="pc-split-card-row-header">
                    <div className="pc-split-card-body-info">
                      <h4 className="pc-split-card-name-title">
                        {cleanTitle}
                      </h4>
                    </div>
                    <p className="pc-split-card-snippet">
                      {route.description?.length > 90
                        ? `${route.description.substring(0, 90)}...`
                        : route.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}; 




// ==========================================
// COMPONENTE: PCLAYOUT PRINCIPAL
// ==========================================
const PCLayout = ({ slug: propSlug, viewMode = false, templateData: initialData }) => {
  const { slug: urlSlug } = useParams();
  const slug = propSlug || urlSlug;

  // Estados
  const [templateData, setTemplateData] = useState(initialData || null);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [currentPackageIndex, setCurrentPackageIndex] = useState(0);
  const [showFormularioModal, setShowFormularioModal] = useState(false);
  const [showCompetitorsModal, setShowCompetitorsModal] = useState(false);
  const [showTimesModal, setShowTimesModal] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState({});


  // Función para toggle de addon
// Función para toggle de addon
const toggleAddon = (pkgId, addonId, addonPrice) => {
  setSelectedAddons(prev => {
    const pkgAddons = prev[pkgId] || {};
    const currentQty = parseInt(pkgAddons[addonId]) || 0;
    
    // Encontramos el paquete para saber el número de competidores
    const pkg = packages.find(p => String(p.id_package || p.id) === String(pkgId));
    const numCompetitors = pkg ? (parseInt(pkg.number_of_competitors) || 1) : 1;
    
    // Si ya tiene cantidad > 0, lo quitamos (0). Si es 0, lo ponemos al máximo (numCompetitors)
    const newQty = currentQty > 0 ? 0 : numCompetitors;
    
    const newPkgAddons = {
      ...pkgAddons,
      [addonId]: newQty
    };
    
    // Si no hay addons seleccionados (todos 0 o false), eliminamos la entrada del paquete
    if (Object.values(newPkgAddons).every(v => parseInt(v) === 0 || v === false)) {
      const newState = { ...prev };
      delete newState[pkgId];
      return newState;
    }
    
    return {
      ...prev,
      [pkgId]: newPkgAddons
    };
  });
};

// Función para calcular precio total con addons
const calcularPrecioConAddons = (pkg) => {
  const baseAmount = parseFloat(pkg.amount) || 0;
  const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
  
  let extraPerPerson = 0;
  if (pkg.addons) {
    pkg.addons.forEach((addon, idx) => {
      const addonId = addon.id || idx;
      const qty = parseInt(pkgAddons[addonId]) || 0;
      if (qty > 0 && addon.extra_amount) {
        // 🔥 Sumamos el precio UNITARIO del addon (por persona), no el total
        extraPerPerson += parseFloat(addon.extra_amount);
      }
    });
  }
  // 🔥 Devuelve el precio POR PERSONA (base + addons por persona)
  return (baseAmount + extraPerPerson).toFixed(2);
};

const formatSmart = (amount, currency = '') => {
  const num = parseFloat(amount);
  if (isNaN(num)) return currency ? `0 ${currency}` : '0';
  // Si tiene decimales significativos, mostrarlos. Si no, solo entero
  const formatted = num % 1 !== 0 ? num.toFixed(2) : num.toFixed(0);
  return currency ? `${formatted} ${currency}` : formatted;
};

  // Hooks
  const { colors } = useColors(slug);

  const banner = useBanner({
    initialLogo: templateData?.banner?.logo || '',
    initialText: templateData?.banner?.text || '',
    initialBackground: templateData?.banner?.background || '',
    initialButtonText: 'Inscríbete',
    slug,
    isEditable: false,
  });

  const { content: infoContent } = useInfo(slug);
  const { routes } = useRoutes(slug);
  const { 
    competitors, 
    filteredCompetitors,
    isLoading: isLoadingCompetitors,
    isListOcultaPublic,
    error: competitorsError,
    categoryColumnName,
    visibleColumns,
    refreshCompetitors,
    normalizeCategory,
    capitalizeCategory,
    getCompetitorCategory,
    getCompetitorDorsal,
  } = useCompetitors(slug, false);
  const { packages, getCompetitorLabel } = usePackages(slug);
  const { sponsors } = useSponsors(slug);
  const { images } = useGallery(slug);
  
  // Dentro del componente PCLayout, después de los hooks

// 🆕 Calcular fecha de oferta formateada
const offerDateFormatted = useMemo(() => {
  if (!packages || packages.length === 0) return null;
  
  // 🔍 CAMBIA 'endDate' POR EL NOMBRE REAL DE TU PROPIEDAD
  const firstPackageWithOffer = packages.find(pkg => pkg.endDate);
  
  if (!firstPackageWithOffer?.endDate) return null;
  
  try {
    const date = new Date(firstPackageWithOffer.endDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  } catch (error) {
    console.error('Error formateando fecha de oferta:', error);
    return null;
  }
}, [packages]);

  // Cargar datos
  useEffect(() => {
    if (!initialData && slug) {
      const fetchData = async () => {
        try {
          const response = await api.get(`/admin/template/${slug}`);
          
          setTemplateData(response.data);
          setIsLoading(false);
        } catch (error) {
          console.error('Error:', error);
          setIsLoading(false);
        }
      };
      fetchData();
    } else if (initialData) {
      setIsLoading(false);
    }
  }, [slug, initialData]);

  useEffect(() => {
    if (refreshCompetitors) {
      refreshCompetitors();
    }
  }, [refreshCompetitors]);

  // Handlers
  const handleInscribeteClick = () => {
    setShowFormularioModal(true);
  };


// ==========================================
// CONTADOR REGRESIVO (Versión simplificada)
// ==========================================
const { eventData, loading: eventDataLoading } = useEventData(slug);


  // ==========================================
  // CARRUSEL DE PAQUETES
  // ==========================================
  // En la sección de paquetes, filtrar los cerrados:
const paquetesDisponibles = useMemo(() => {
  if (!packages || packages.length === 0) return [];
  return packages.filter(pkg => !pkg.is_closed);
}, [packages]);

// También obtener paquetes cerrados para mostrar mensaje
const paquetesCerrados = useMemo(() => {
  if (!packages || packages.length === 0) return [];
  return packages.filter(pkg => pkg.is_closed);
}, [packages]);

  // Sponsors list
  const getSponsorsList = () => {
    if (sponsors.length === 0) return [];
    let list = [...sponsors];
    while (list.length < 12) {
      list = [...list, ...sponsors];
    }
    return list;
  };

  // Payment methods
  const paymentMethods = [
    "/icons/visa.svg",
    "/icons/mastercard.svg",
    "/icons/qr.svg"
  ];

// ==========================================
// COMPETITORS MODAL - PC LAYOUT (TRAPECIO + GRID + BUSCADOR)
// ==========================================
const CompetitorsModal = () => {
  
  // 1. ⚠️ TODOS LOS HOOKS AL INICIO (Jamás poner un return antes de los Hooks)
  const [viewMode, setViewMode] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');

  // Colors del evento
  const primaryAcc = colors?.primaryColor || '#bc00dd';
  const secondaryAcc = colors?.secondaryColor || '#00c6fb';
  

  // 🟢 Detección local de columna de categoría (fallback si useCompetitors no la detecta)
  const categoriaColLocal = useMemo(() => {
    if (categoryColumnName) return categoryColumnName;
    const sample = competitors[0] || {};
    const keys = Object.keys(sample);
    // Buscar cualquier clave que contenga "categor" o "category"
    return keys.find((k) => /categor/i.test(k)) || null;
  }, [categoryColumnName, competitors]);

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toString()
      .trim()
      .replace(/^["']|["']$/g, '')  // Elimina comillas al inicio y final
      .replace(/\s+/g, ' ')         // Espacios múltiples a uno solo
      .trim();
  };

  // Helper para verificar si una columna está visible
const isColumnVisible = (columnName) => {
  if (!visibleColumns || visibleColumns.length === 0) return true; // Fallback
  return visibleColumns.some(col => 
    col.toLowerCase() === columnName.toLowerCase()
  );
};

// Helper para detectar columnas por patrón
const findColumnValue = (comp, patterns) => {
  for (const pattern of patterns) {
    const key = Object.keys(comp).find(k => 
      k.toLowerCase().includes(pattern.toLowerCase())
    );
    if (key && comp[key]) return comp[key];
  }
  return null;
};

  // Helper para obtener categoría
  const getCompetitorCategory = (comp) => {
    if (!categoriaColLocal) return null;
    const rawCategory = comp[categoriaColLocal];
    return rawCategory ? rawCategory.toString().trim() : null;
  };

  // Helper para obtener categoría completa (Paquete + Categoría)
  const getFullCategory = (comp) => {
    const paquete = normalizeText(comp.paquete || comp.package || '').toString().trim();
    const categoria = normalizeText(getCompetitorCategory(comp) || '');
    if (paquete && categoria) return `${paquete} - ${categoria}`;
    if (categoria) return categoria;
    if (paquete) return paquete;
    return null;
  };

  // Obtener la fecha de fin del primer paquete para la frase global
  const firstPackageEndDateStr = packages && packages.length > 0 ? packages[0].endDate : null;

  const formatOfferDate = (dateString) => {
    if (!dateString) return null;
    try {
      // Forzar formato YYYY-MM-DD para evitar problemas de zona horaria al parsear
      const [year, month, day] = dateString.split('T')[0].split('-');
      if (!year || !month || !day) return null;
      const date = new Date(year, parseInt(month) - 1, day);
      if (isNaN(date.getTime())) return null;

      const monthName = date.toLocaleDateString('es-ES', { month: 'long' });
      return `${date.getDate()} de ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
    } catch (e) {
      return null;
    }
  };

 

  // Helper para obtener dorsal
  const getCompetitorDorsal = (comp) => {
    return comp.dorsal || comp.bib || comp.numero || null;
  };

  // 🔍 Detectar columnas dinámicas de nombre/apellido/club
  const colKeys = useMemo(() => {
    const sample = competitors[0] || {};
    const keys = Object.keys(sample);
    const nombreCol =
      keys.find((k) => /nombre|name|nombres/i.test(k) && !/apellido|lastname/i.test(k)) || null;
    const apellidoCol =
      keys.find((k) => /apellido|apellidos|lastname/i.test(k)) || null;
    const clubCol =
      keys.find((k) => /club|equipo|team/i.test(k)) || null;
    return { nombreCol, apellidoCol, clubCol };
  }, [competitors]);

  const getCompetitorNames = (comp) => {
    const names = [];
  
    // Ciclista 1
    const nombre1 = normalizeText(comp.nombre_ciclista_1 || '');
    const apellido1 = normalizeText(comp.apellido_ciclista_1 || '');
  
    if (nombre1 || apellido1) {
      names.push(`${nombre1} ${apellido1}`.trim());
    }
  
    // Ciclista 2
    const nombre2 = normalizeText(comp.nombre_ciclista_2 || '');
    const apellido2 = normalizeText(comp.apellido_ciclista_2 || '');
  
    if (nombre2 || apellido2) {
      names.push(`${nombre2} ${apellido2}`.trim());
    }
  
    // Si no existen las columnas específicas de ciclistas,
    // mantenemos compatibilidad con los eventos normales.
    if (names.length === 0) {
      const firstName = normalizeText(
        comp[colKeys.nombreCol] ||
        comp.nombre ||
        comp.name ||
        comp.first_name ||
        ''
      );
  
      const lastName = normalizeText(
        comp[colKeys.apellidoCol] ||
        comp.apellido ||
        comp.lastname ||
        comp.last_name ||
        ''
      );
  
      if (firstName || lastName) {
        names.push(`${firstName} ${lastName}`.trim());
      }
    }
  
    return names;
  };

 // ✅ useMemo SIEMPRE arriba, sin condiciones de retorno previas
const searchedCompetitors = useMemo(() => {
  const baseList = filteredCompetitors.length > 0
    ? filteredCompetitors
    : competitors;
    

  if (!searchTerm.trim()) return baseList;

 

  const term = searchTerm.toLowerCase().trim();

  return baseList.filter((comp) => {
    // 🔎 Obtener nombres de todos los ciclistas disponibles
    const competitorNames = getCompetitorNames(comp);

    // Unir nombres para permitir buscar por cualquiera
    const fullName = competitorNames
      .join(' ')
      .toLowerCase();

    const rawCat = getCompetitorCategory(comp);

    const category = normalizeText(
      rawCat ||
      comp.categoria ||
      comp.category ||
      comp.paquete ||
      ''
    ).toLowerCase();

    const club = normalizeText(
      comp[colKeys.clubCol] ||
      comp.club ||
      comp.team ||
      comp.equipo ||
      ''
    ).toLowerCase();

    const bib = (getCompetitorDorsal(comp) || '')
      .toString()
      .toLowerCase();


    return (
      fullName.includes(term) ||
      category.includes(term) ||
      club.includes(term) ||
      bib.includes(term)
    );
  });
}, [
  searchTerm,
  filteredCompetitors,
  competitors,
  categoriaColLocal,
  colKeys
]);
  
  const displayCompetitors = searchedCompetitors;

  const competitorsByCategory = useMemo(() => {
    return displayCompetitors.reduce((acc, comp) => {
      // 🔥 Obtener paquete + categoría como clave única
      const paquete = normalizeText(comp.paquete || comp.package || '').toString().trim();
      const rawCategory = normalizeText(getCompetitorCategory(comp) || '');
      
      // Construir clave combinada: "10K - Máster A" o solo "Máster A" si no hay paquete
      let displayName = rawCategory ? capitalizeCategory(rawCategory) : 'Sin Categoría';
      if (paquete) {
        displayName = `${paquete} - ${displayName}`;
      }
      
      const normalizedKey = paquete && rawCategory 
        ? `${paquete} - ${rawCategory}`.toLowerCase()
        : (rawCategory || 'Sin Categoría');
      
      if (!acc[normalizedKey]) {
        acc[normalizedKey] = {
          displayName: displayName,
          competitors: []
        };
      }
      
      acc[normalizedKey].competitors.push(comp);
      return acc;
    }, {});
  }, [displayCompetitors, categoriaColLocal]);

  const sortedCategories = Object.keys(competitorsByCategory).sort();
  const hasCategoryField = categoriaColLocal !== null;

  // 2. 🛡️ AHORA SÍ: Returns anticipados (Lista oculta/privada)
  if (isListOcultaPublic) {
    return (
      <div className="pc-modal-overlay" onClick={() => setShowCompetitorsModal(false)}>
        <div className="pc-modal-content pc-competitors-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', padding: '40px 20px' }}>
          <button className="pc-modal-close" onClick={() => setShowCompetitorsModal(false)}>CERRAR</button>
          <span style={{ fontSize: '48px' }}>🔒</span>
          <h3 className="pc-modal-title" style={{ marginTop: '15px', color: '#374151' }}>Lista de Participantes Privada</h3>
          <p style={{ color: '#6b7280', marginTop: '5px' }}>Por disposición de los organizadores del evento, el listado público no se encuentra disponible.</p>
        </div>
      </div>
    );
  }

  // Helper para renderizar una tarjeta individual
  const renderCompetitorCard = (comp, index) => {
    const hasVisibleConfig = visibleColumns && visibleColumns.length > 0;
  
    // Si no hay configuración explícita, se consideran visibles por defecto los campos principales
    const isVisible = (colKey) => !hasVisibleConfig || isColumnVisible(colKey);
  
    // --- LÍNEA 1: Campos Principales ---
    const showDorsal = isVisible('dorsal') || isVisible('bib') || isVisible('numero');
    const showName =
  isVisible('nombre') ||
  isVisible('nombres') ||
  isVisible('apellido') ||
  isVisible('apellidos') ||
  isVisible('nombre_ciclista_1') ||
  isVisible('apellido_ciclista_1') ||
  isVisible('nombre_ciclista_2') ||
  isVisible('apellido_ciclista_2');
    const showCategory = isVisible('categoria') || isVisible('category') || isVisible('paquete') || isVisible('categorias');
    const showClub = isVisible('club') || isVisible('equipo') || isVisible('team') || isVisible('clubequipo');
  
    // Obtención de valores para la Línea 1
    const competitorNames = getCompetitorNames(comp);

const fullName = competitorNames.length > 0
  ? competitorNames.join(' / ')
  : 'Atleta';
    const fullCat = getFullCategory(comp);
    const categoria = fullCat ? capitalizeCategory(fullCat) : 'General';
    const club = colKeys.clubCol ? (comp[colKeys.clubCol] || 'Independiente') : (comp.club || comp.team || comp.equipo || 'Independiente');
    
    // Fallback de dorsal a index + 1 si no ha sido asignado aún
    const rawDorsal = getCompetitorDorsal(comp);
    const dorsalDisplay = rawDorsal ? rawDorsal : (index + 1);
  
    // --- LÍNEA 2: Campos Secundarios Dinámicos ---
    // Identificadores de las columnas que corresponden a la línea 1 para excluirlos de la línea 2
    const primaryKeys = [
      'dorsal', 'bib', 'numero',
      'nombre', 'name', 'first_name', 'apellido', 'lastname', 'last_name',
      'categoria', 'category', 'paquete',
      'club', 'equipo', 'team',
      'id', 'slug'
    ];
  
    // Filtramos dinámicamente visibleColumns para obtener únicamente los campos extras
    const secondaryColumns = hasVisibleConfig
      ? visibleColumns.filter(col => !primaryKeys.some(pKey => col.toLowerCase().includes(pKey)))
      : [];
  
    return (
      <div
        key={comp.id || index}
        className="pc-competitor-row-wrapper"
        style={{ '--accent-1': primaryAcc, '--accent-2': secondaryAcc }}
      >
        <div className="pc-trapezoid-body">
        {showDorsal && (
              <span className="pc-competitor-dorsal">
                {dorsalDisplay}
              </span>
            )}
            <div className="pc-text-container">
          {/* LÍNEA 1 */}
          <div className="pc-line line-primary">

            {showName && (
              <span className="pc-competitor-fullname" title={fullName}>
                {fullName}
              </span>
            )}
            {showCategory && (
              <span className="pc-competitor-category" title={categoria}>
                {categoria}
              </span>
            )}
            {showClub && (
              <span className="pc-competitor-club" title={club}>
                {club}
              </span>
            )}
          </div>
  
          {/* LÍNEA 2 (Solo se dibuja si hay campos adicionales configurados) */}
          {secondaryColumns.length > 0 && (
            <div className="pc-line line-secondary">
              {secondaryColumns.map((colKey) => {
                const val = comp[colKey];
                if (!val) return null;
                return (
                  <span key={colKey} className="pc-competitor-extra-field" title={`${colKey}: ${val}`}>
                    <small className="pc-field-label">{colKey}:</small> {val}
                  </span>
                );
              })}
            </div>
          )}
          </div>
        </div>
        
        <div className="pc-external-lines">
          <div className="line line-1" />
          <div className="line line-2" />
          <div className="line line-3" />
        </div>
      </div>
    );
  };

  return (
    <div className="pc-modal-overlay" onClick={() => setShowCompetitorsModal(false)}>
      <div className="pc-modal-content pc-competitors-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header Modal */}
        <div className="pc-modal-header-container">
          <div>
            <span className="pc-modal-subtitle" style={{ color: primaryAcc }}>START LIST OFICIAL</span>
            <h3 className="pc-modal-title"> Atletas Inscritos</h3>
          </div>
          <button className="pc-modal-close" onClick={() => setShowCompetitorsModal(false)}>CERRAR</button>
        </div>

        {/* 🔍 BARRA DE BÚSQUEDA INTEGRADA */}
        <div className="pc-search-box-wrapper">
          <div className="pc-search-input-container">
            <span className="pc-search-icon">🔍</span>
            <input
              type="text"
              className="pc-search-input"
              placeholder="Buscar por nombre, club o dorsal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="pc-search-clear-btn" 
                onClick={() => setSearchTerm('')}
                title="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>
          {searchTerm && (
            <span className="pc-search-results-count">
              {displayCompetitors.length} {displayCompetitors.length === 1 ? 'resultado' : 'resultados'}
            </span>
          )}
        </div>
        
        {/* Tabs de vista */}
        {hasCategoryField && (
          <div className="pc-competitors-tabs">
            <button
              className={`pc-tab-btn ${viewMode === 'general' ? 'active' : ''}`}
              onClick={() => setViewMode('general')}
              style={{
                borderBottom: viewMode === 'general' ? `3px solid ${primaryAcc}` : '3px solid transparent',
                color: viewMode === 'general' ? primaryAcc : '#6b7280',
              }}
            >
               General ({displayCompetitors.length})
            </button>
            <button
              className={`pc-tab-btn ${viewMode === 'categorias' ? 'active' : ''}`}
              onClick={() => setViewMode('categorias')}
              style={{
                borderBottom: viewMode === 'categorias' ? `3px solid ${primaryAcc}` : '3px solid transparent',
                color: viewMode === 'categorias' ? primaryAcc : '#6b7280',
              }}
            >
               Por Categorías
            </button>
          </div>
        )}

        {/* Contenido principal */}
        {isLoadingCompetitors ? (
          <div className="pc-modal-loading">Cargando lista de inscritos...</div>
        ) : competitorsError ? (
          <div className="pc-modal-error">Error al cargar la lista</div>
        ) : displayCompetitors.length > 0 ? (
          <div className="pc-modal-scroll-body">
            
            {/* VISTA GENERAL (GRID DE 2 COLUMNAS) */}
            {viewMode === 'general' && (
              <div className="pc-competitors-cards-grid">
                {displayCompetitors.map((comp, index) => renderCompetitorCard(comp, index))}
              </div>
            )}

            {/* VISTA POR CATEGORÍAS */}
            {viewMode === 'categorias' && hasCategoryField && (
              <div className="pc-categories-wrapper">
                {sortedCategories.map((normalizedKey) => {
                  const group = competitorsByCategory[normalizedKey];
                  return (
                    <div key={normalizedKey} className="pc-category-group-section">
                      <h4 className="pc-category-group-title" style={{ borderColor: primaryAcc, color: primaryAcc }}>
                        {group.displayName} 
                        <span>({group.competitors.length} {group.competitors.length === 1 ? 'inscrito' : 'inscritos'})</span>
                      </h4>
                      <div className="pc-competitors-cards-grid">
                        {group.competitors.map((comp, index) => renderCompetitorCard(comp, index))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        ) : (
          <p className="pc-modal-empty">
            {searchTerm ? 'No se encontraron atletas con esa búsqueda.' : 'No hay inscritos aún. ¡Sé el primero en inscribirte!'}
          </p>
        )}
      </div>
    </div>
  );
};

// ==========================================
// TIEMPOS MODAL - VISTA PÚBLICA
// ==========================================
const TimesModal = () => (
  <div className="pc-modal-overlay" onClick={() => setShowTimesModal(false)}>
    <div 
      className="pc-modal-content pc-live-results-modal" 
      onClick={(e) => e.stopPropagation()}
    >
      <button 
        className="pc-modal-close" 
        onClick={() => setShowTimesModal(false)}
      >
        CERRAR
      </button>
      <LiveResults 
        slug={slug} 
        nombreCompetencia="Resultados en Vivo" 
      />
    </div>
  </div>
);


// ==========================================
// COMPONENTE: EVENT SCHEDULE TIMELINE
// ==========================================
// PCLayout.js

// 1. Importar los iconos que usaremos (ya los tienes importados, solo asegúrate de tener este mapeo)
const scheduleIconMap = {
  'FaShoppingBag': FaShoppingBag,
  'FaUsers': FaUsers,
  'FaFlag': FaFlag,
  'FaFlagCheckered': FaFlagCheckered,
  'FaTrophy': FaTrophy,
  'FaClock': FaClock,
  'FaMapMarkerAlt': FaMapMarkerAlt
};

// ==========================================
// COMPONENTE: EVENT SCHEDULE TIMELINE (DINÁMICO)
// ==========================================
const EventScheduleTimeline = ({ event, eventData, colors }) => {
  // 🆕 PRIORIDAD 1: schedule_data de featured_events (lo que configuró el admin)
  const scheduleData = eventData?.scheduleData;
  
  // PRIORIDAD 2: Fallback al cálculo por fecha (compatibilidad hacia atrás)
  const mainEventDateStr = event?.end_date || event?.event_date || eventData?.endDate;
  
  const getCalculatedDateStr = (baseDateStr, daysToSubtract = 0) => {
    if (!baseDateStr) return "Por confirmar";
    try {
      const date = new Date(baseDateStr);
      if (isNaN(date.getTime())) return "Por confirmar";
      if (daysToSubtract > 0) {
        date.setDate(date.getDate() - daysToSubtract);
      }
      const options = { weekday: 'long', day: 'numeric', month: 'short' };
      let formatted = date.toLocaleDateString('es-ES', options);
      return formatted.charAt(0).toUpperCase() + formatted.slice(1).replace('.', '');
    } catch (e) {
      return "Por confirmar";
    }
  };
  
  const kitDate = getCalculatedDateStr(mainEventDateStr, 2);
  const raceDayDate = getCalculatedDateStr(mainEventDateStr, 0);
  
  // Fallback si no hay schedule_data configurado
  const fallbackSchedule = [
    { id: 1, title: "Entrega de Dorsales", icon: "FaShoppingBag", date: kitDate, time: "10:00 - 18:00" },
    { id: 2, title: "Concentración", icon: "FaUsers", date: raceDayDate, time: "06:30 AM" },
    { id: 3, title: "Largada Oficial", icon: "FaFlag", date: raceDayDate, time: "07:30 AM" },
    { id: 4, title: "Cierre de Meta", icon: "FaFlagCheckered", date: raceDayDate, time: "11:00 AM" },
    { id: 5, title: "Premiación", icon: "FaTrophy", date: raceDayDate, time: "11:30 AM" }
  ];
  
  // 🎯 USAR scheduleData si existe y es válido
  const finalSchedule = (scheduleData && Array.isArray(scheduleData) && scheduleData.length > 0)
    ? scheduleData
    : fallbackSchedule;
  
  // Mapeo de iconos
  const scheduleIconMap = {
    'FaShoppingBag': FaShoppingBag,
    'FaUsers': FaUsers,
    'FaFlag': FaFlag,
    'FaFlagCheckered': FaFlagCheckered,
    'FaTrophy': FaTrophy,
    'FaClock': FaClock,
    'FaMapMarkerAlt': FaMapMarkerAlt
  };
  
  const primaryColor = colors?.primaryColor || '#bc00dd';
  const gradientStyles = {
    background: `linear-gradient(135deg, ${primaryColor} 0%, #23034f 100%)`
  };
  
  return (
    <section className="pc-timeline-section">
      <div className="pc-timeline-container">
        <div className="pc-timeline-header">
          <h2 className="pc-timeline-main-title">Cronograma del Evento</h2>
        </div>
        <div className="pc-timeline-row-wrapper">
          {finalSchedule.map((item, index) => {
            const IconComponent = scheduleIconMap[item.icon] || FaClock;
            
            return (
              <React.Fragment key={item.id || index}>
                <div className="pc-timeline-node">
                  <div className="pc-timeline-icon-circle" style={gradientStyles}>
                    <IconComponent className="pc-tl-icon" />
                  </div>
                  <div className="pc-timeline-content-box">
                    {item.badge && <span className="pc-tl-node-badge">{item.badge}</span>}
                    <h4 className="pc-tl-node-title">{item.title}</h4>
                    <p className="pc-tl-node-date">{item.date}</p>
                    <p className="pc-tl-node-time">⏰ {item.time}</p>
                  </div>
                </div>
                {index < finalSchedule.length - 1 && (
                  <div className="pc-timeline-arrow-connector"></div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};
  // ==========================================
  // CALL TO ACTION
  // ==========================================

  const CallToActionBanner = ({ event, colors, onInscribeteClick }) => {
  
    // Extraer la ubicación de la misma forma que en EventsSection
    const getLocationCity = () => {
      if (event?.location_city) return event.location_city;
      if (event?.location_name) return event.location_name;
      return "tu ciudad";
    };
  
    const handleOpenModal = () => {
      if (onInscribeteClick) {
        onInscribeteClick();  // ← Abre el modal
      }
    };
  
    return (
      <section className="pc-cta-banner-section">
        {/* El fondo combina la imagen fija de public/images y el degradado */}
        <div className="pc-cta-banner-bg-overlay">
          <div className="pc-cta-container">
            
            {/* BLOQUE IZQUIERDO: TEXTOS EMOCIONALES */}
            <div className="pc-cta-text-side">
              <h3 className="pc-cta-subtitle">TU PRÓXIMO RETO</h3>
              <h2 className="pc-cta-main-title">COMIENZA AQUÍ</h2>
              <p className="pc-cta-description">
                No te quedes fuera de la carrera más esperada de <span className="pc-cta-highlight-city">{getLocationCity()}</span>.
              </p>
            </div>
  
            {/* BLOQUE DERECHO: ACCIÓN Y SEGURIDAD */}
            <div className="pc-cta-action-side">
              <button 
                onClick={handleOpenModal}
                className="pc-cta-btn-submit"
                style={{ '--primary-event-color': colors?.primaryColor || '#bc00dd' }}
              >
                Inscríbete Ahora
              </button>
              
              <div className="pc-cta-secure-badge">
                <FaLock className="pc-cta-lock-icon" />
                <span>Pago 100% Seguro</span>
              </div>
            </div>
  
          </div>
        </div>
      </section>
    );
  };




  // ==========================================
  // RENDER
  // ==========================================
  if (isLoading) return <div className="pc-loading">Cargando...</div>;
  if (!templateData) return <div className="pc-error">No se encontró el evento</div>;

  const dynamicStyles = {
    '--pc-dynamic-primary': colors?.primaryColor || 'var(--pc-primary)',
    '--pc-dynamic-secondary': colors?.secondaryColor || 'var(--pc-secondary)',
    '--pc-banner-bg-img': `url(${banner.backgroundPreview})`
  };

  return (
    <div className="pc-landing-container" style={dynamicStyles}>
      
      {/* DEFINICIÓN GLOBAL DE GRADIENTES SVG */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#bc00dd" />
            <stop offset="100%" stopColor="#00c6fb" />
          </linearGradient>
        </defs>
      </svg>

      {/* 1. NAVBAR */}
      <NavbarPC 
        slug={slug}
        colors={colors}
        onInscribeteClick={handleInscribeteClick}
        onCompetitorsClick={() => setShowCompetitorsModal(true)}
        onTimesClick={() => setShowTimesModal(true)}
        showFormulario={showFormularioModal}
        refreshCompetitors={refreshCompetitors}
      />

{/* 2. BANNER CONTENEDOR - VERSIÓN SIMPLIFICADA SIN PAQUETES */}
<header className="pc-banner-wrapper">
  <div className="pc-banner-cover">
    <div className="pc-banner-overlay" />
    
    <div className="pc-banner-grid">
      
      {/* LADO IZQUIERDO - Ahora ocupa todo el ancho */}
      <div className="pc-banner-left">
        {banner.logoPreview && (
          <img src={banner.logoPreview} alt="Logo" className="pc-banner-logo" />
        )}
        
        <h1 className="pc-banner-text">{banner.text}</h1>
        
        {/* METADATA DEL EVENTO */}
        <div className="pc-event-meta">
          <div className="pc-meta-item">
            <FaMapMarkerAlt className="pc-meta-icon pc-color-primary" />
            <span>
              {eventData?.locationName || 
               eventData?.locationCity || 
               'Ubicación por confirmar'}
            </span>
          </div>
          <div className="pc-meta-item">
            <FaCalendarAlt className="pc-meta-icon pc-color-primary" />
            <span>
              {eventData?.endDate ? 
                new Date(eventData.endDate).toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })
                : 'Fecha por confirmar'}
            </span>
          </div>
        </div>

        {/* BOTÓN VER CONVOCATORIA */}
        <button 
          className="pc-banner-info-btn"
          style={{ 
            backgroundColor: 'white',
            border: `2px solid ${colors?.primaryColor || '#9370DB'}`,
            color: colors?.primaryColor || '#9370DB'
          }}
          onClick={() => setShowInfoModal(true)}
        >
          <FaInfoCircle className="pc-banner-info-icon" />
          Ver Convocatoria
        </button>

        {/* CONTADOR REGRESIVO */}
        <div className="pc-countdown-wrapper">
          {eventDataLoading ? (
            <div className="pc-countdown-loading">Cargando cuenta regresiva...</div>
          ) : (
            <EventCountdown endDate={eventData?.endDate} variant="pc" />
          )}
        </div>
      </div>

    </div>
  </div>
</header>

{/* 2.5 SECCIÓN DE PAQUETES - INDEPENDIENTE DEL BANNER */}

<section className="pc-packages-section">
  <div className="pc-packages-container">
    <h2 className="pc-packages-section-title" style={{ color: colors?.primaryColor || '#9370DB' }}>
      ELIGE TU PAQUETE E INSCRÍBETE
    </h2>
    
    {/* Subtítulo con fecha global del primer paquete */}
    {offerDateFormatted && (
      
      <p style={{
        textAlign: 'center',
        fontSize: '1.05rem',
        fontWeight: '600',
        color: '#6b7280',
        marginTop: '-10px',
        marginBottom: '25px'
      }}>
        Inscripciones hasta el <span style={{ color: '#bc00dd', fontWeight: '700' }}>{offerDateFormatted}</span>
      </p>
      
    )}
    
    {/* Mostrar todos los paquetes (disponibles y cerrados) */}
    {packages.length > 0 ? (
      <div className="pc-packages-grid">
        {packages.map((pkg, index) => {
          const isClosed = pkg.is_closed;
          const totalAmount = calcularPrecioConAddons(pkg);
          
          return (
            <div 
              key={pkg.id_package || index} 
              className={`pc-package-card-inline ${isClosed ? 'pc-package-card--closed' : ''}`}
            >
              {/* Overlay si está cerrado */}
              {isClosed && (
                <div className="pc-package-overlay">
                  <span className="pc-package-overlay-text">INSCRIPCIONES CERRADAS</span>
                </div>
              )}

              {/* HEADER con badge */}
              <div className="pc-package-header-inline">
                <h3 className="pc-package-title-inline">{pkg.title}</h3>
                <span className="pc-package-badge-inline" style={{ 
                  background: isClosed 
                    ? '#6c757d' 
                    : `linear-gradient(135deg, ${colors?.primaryColor || '#bc00dd'}, ${colors?.secondaryColor || '#00c6fb'})` 
                }}>
                  {getCompetitorLabel(pkg.number_of_competitors)}
                </span>
              </div>

              {/* IMAGEN con opacidad si está cerrado */}
              <div className="pc-package-image-inline">
                {pkg.image ? (
                  <img 
                    src={pkg.image} 
                    alt={pkg.title} 
                    style={{ opacity: isClosed ? 0.5 : 1 }} 
                  />
                ) : (
                  <div className="pc-package-image-placeholder-inline">
                    <LuMedal style={{ fontSize: '3rem', color: isClosed ? '#6c757d' : (colors?.primaryColor || '#9370DB') }} />
                  </div>
                )}
              </div>

              {/* PRECIO con tachado si está cerrado */}
              <div className="pc-package-price-inline">
                <div className="pc-price-main">
                  <span className="pc-price-currency">{pkg.currency}</span>
                  <span className={`pc-price-amount ${isClosed ? 'pc-price-amount--closed' : ''}`}>
                    {formatSmart(totalAmount * pkg.number_of_competitors)}
                  </span>
                </div>

                {pkg.number_of_competitors > 1 && (
                  <div className="pc-price-per-runner">
                    <span className="pc-price-per-runner-label">
                      {formatSmart(totalAmount)} {pkg.currency}
                    </span>
                    <span className="pc-price-per-runner-text">por corredor</span>
                  </div>
                )}

                {pkg.conversion_rate && pkg.amount && (
                  <span className="pc-price-usd">
                    ≈ {(parseFloat(totalAmount * pkg.number_of_competitors) / pkg.conversion_rate).toFixed(2)} USD
                  </span>
                )}

                {/* Badge de AGOTADO */}
                {isClosed && (
                  <div className="pc-package-sold-out-badge">
                    <span>🔒 AGOTADO</span>
                  </div>
                )}
              </div>

              {/* DESCRIPCIÓN */}
              <p className="pc-package-desc-inline">{pkg.description}</p>


           {/* ADDONS deshabilitados si está cerrado */}
{pkg.addons && pkg.addons.length > 0 && (
  <div className="pc-package-addons-inline">
    <span className="pc-addons-title">Extras:</span>
    {pkg.addons.map((addon, idx) => {
      const addonId = addon.id || idx;
      const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
      const currentQty = parseInt(pkgAddons[addonId]) || 0;
      const isChecked = currentQty > 0;
      const numCompetitors = parseInt(pkg.number_of_competitors) || 1;
      const unitPrice = parseFloat(addon.extra_amount || 0);
      
      // 🔥 Si está marcado, mostramos el total. Si no, mostramos el unitario para que el usuario sepa el precio.
      const displayPrice = isChecked ? (unitPrice * numCompetitors) : unitPrice;
      
      return (
        <label key={idx} className={`pc-addon-item-inline ${isClosed ? 'pc-addon-item--disabled' : ''}`}>
          <input 
            type="checkbox"
            checked={isChecked}
            onChange={() => !isClosed && toggleAddon(pkg.id_package || pkg.id, addonId, addon.extra_amount)}
            disabled={isClosed}
          />
          <span>{addon.label}</span>
          <span className="pc-addon-price">
            +{formatSmart(displayPrice, pkg.currency)}
            {isChecked && numCompetitors > 1 && (
              <span className="pc-addon-qty-badge">x{numCompetitors}</span>
            )}
            {!isChecked && numCompetitors > 1 && (
              <span className="pc-addon-qty-hint" style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '4px' }}>c/u</span>
            )}
          </span>
        </label>
      );
    })}
  </div>
)}

              {/* BOTÓN - deshabilitado si está cerrado */}
              <button 
                className={`pc-package-btn-inline ${isClosed ? 'pc-package-btn--disabled' : ''}`}
                style={{ 
                  background: isClosed 
                    ? '#6c757d' 
                    : `linear-gradient(135deg, ${colors?.primaryColor || '#bc00dd'}, ${colors?.secondaryColor || '#00c6fb'})`,
                  cursor: isClosed ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  if (isClosed) return;
                  const pkgAddons = selectedAddons[pkg.id_package || pkg.id] || {};
                  const selectedAddonsMap = {};
                  
                  if (pkg.addons) {
                    pkg.addons.forEach(addon => {
                      const addonId = addon.id;
                      // 🔥 Aseguramos que se pase como número entero (ej. 2 para duplas)
                      selectedAddonsMap[addonId] = parseInt(pkgAddons[addonId]) || 0;
                    });
                  }
                  
                  const pkgWithAddons = {
                    ...pkg,
                    selectedAddons: selectedAddonsMap,
                    totalAmount: calcularPrecioConAddons(pkg)
                  };
                  setSelectedPackage(pkgWithAddons);
                  setShowFormularioModal(true);
                }}
                disabled={isClosed}
              >
                {isClosed ? '🔒 No Disponible' : 'Seleccionar'}
              </button>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="pc-packages-empty">No hay paquetes disponibles</p>
    )}

    {/* PAGO SEGURO */}
    <div className="pc-secure-payment-inline">
      <div className="pc-secure-label-inline">
        <FaLock className="pc-secure-lock" style={{ color: colors?.primaryColor || '#9370DB' }} />
        <span>Pago 100% seguro</span>
      </div>
      <div className="pc-secure-methods-inline">
        {paymentMethods.map((icon) => (
          <img key={icon} src={icon} className="pc-pay-icon-inline" alt="" />
        ))}
      </div>
    </div>
  </div>
</section>

      {/* SPONSORS */}
      <section className="pc-sponsors pc-bg-dark">
        <div className="pc-sponsors-slider-container">
          {sponsors.length > 0 ? (
            <div className="pc-sponsors-track">
              {getSponsorsList().map((sponsor, index) => (
                <div key={index} className="pc-sponsor-slide">
                  <img src={sponsor} alt={`Sponsor ${index + 1}`} className="pc-sponsor-item" />
                </div>
              ))}
              {getSponsorsList().map((sponsor, index) => (
                <div key={`dup-${index}`} className="pc-sponsor-slide">
                  <img src={sponsor} alt={`Sponsor ${index + 1}`} className="pc-sponsor-item" />
                </div>
              ))}
            </div>
          ) : (
            <p className="pc-sponsors-empty">No hay patrocinadores aún.</p>
          )}
        </div>
      </section>

      {/* FRANJA DE ICONOS DESTACADOS */}
      <section className="pc-highlight-strip">
        <div className="pc-highlight-container">
          <div className="pc-highlight-item">
            <div className="pc-highlight-icon-wrapper">
              <LuMedal className="pc-highlight-icon" />
            </div>
            <div className="pc-highlight-info">
              <h4>MEDALLA FINISHER</h4>
              <p>Para todos los participantes que crucen la meta.</p>
            </div>
          </div>
          <div className="pc-highlight-item">
            <div className="pc-highlight-icon-wrapper">
              <LuClock className="pc-highlight-icon" />
            </div>
            <div className="pc-highlight-info">
              <h4>CRONOMETRAJE OFICIAL</h4>
              <p>Tiempo oficial con chip de última tecnología.</p>
            </div>
          </div>
          <div className="pc-highlight-item">
            <div className="pc-highlight-icon-wrapper">
              <LuTrendingUp className="pc-highlight-icon" />
            </div>
            <div className="pc-highlight-info">
              <h4>RESULTADOS ONLINE</h4>
              <p>Consulta tus resultados en tiempo real desde cualquier dispositivo.</p>
            </div>
          </div>
          <div className="pc-highlight-item">
            <div className="pc-highlight-icon-wrapper">
              <LuCamera className="pc-highlight-icon" />
            </div>
            <div className="pc-highlight-info">
              <h4>FOTOGRAFÍAS</h4>
              <p>Fotografías oficiales disponibles para todos los corredores.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MODAL INFO */}
      {showInfoModal && (
        <div className="pc-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="pc-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="pc-modal-close" onClick={() => setShowInfoModal(false)}>CERRAR</button>
            <h3 className="pc-modal-title">📋 Convocatoria del Evento</h3>
            <div className="pc-modal-meta">
              <div className="pc-modal-meta-item">
                <FaMapMarkerAlt style={{ color: colors?.primaryColor || '#9370DB' }} />
                <span>{eventData?.location_name || eventData?.location_city || 'Ubicación no definida'}</span>
              </div>
              <div className="pc-modal-meta-item">
                <FaCalendarAlt style={{ color: colors?.primaryColor || '#9370DB' }} />
                <span>
                  {eventData?.endDate 
                    ? new Date(eventData.endDate).toLocaleDateString('es-ES', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })
                    : 'Fecha no definida'}
                </span>
              </div>
            </div>
<div className="pc-modal-info-content">
  {(() => {
    const infoData = templateData?.info;
    if (!infoData) return <p>No hay información disponible.</p>;

    let type = 'html';
    let content = '';

    if (typeof infoData === 'string') {
      try {
        const parsed = JSON.parse(infoData);
        type = parsed.type || 'html';
        content = parsed.content || '';
      } catch (e) {
        content = infoData;
      }
    } else if (typeof infoData === 'object') {
      type = infoData.type || 'html';
      content = infoData.content || '';
    }

    const baseUrl = process.env.REACT_APP_UPLOADS_INFO?.replace(/\/$/, '') || 'http://localhost:5000';

    if (type === 'pdf') {
      return (
        <iframe
          src={`${baseUrl}${content}`}
          width="100%"
          height="800px"
          style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}
          title="Convocatoria PDF"
        />
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  })()}
</div>
          </div>
        </div>
      )}

      {/* 4. FORMULARIO MODAL */}
      {showFormularioModal && (
        <div className="pc-modal-overlay" onClick={() => setShowFormularioModal(false)}>
          <div className="pc-modal-content pc-formulario-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pc-modal-close" onClick={() => setShowFormularioModal(false)}>CERRAR</button>
            <PCFormulario 
              isEditable={false}
              selectedPackage={selectedPackage}
              isFillingForm={true}
              onTabChange={(tab) => {
                if (tab === 'competitors') {
                  setShowFormularioModal(false);
                  setShowCompetitorsModal(true);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* 5. RUTAS */}
      {routes.length > 0 ? (
        <RoutesInteractiveContainer routes={routes} colors={colors} />
      ) : (
        <section className="pc-routes-empty-section">
          <p>Las rutas oficiales del evento se publicarán muy pronto.</p>
        </section>
      )}



      {/* CRONOGRAMA DEL EVENTO */}
      <EventScheduleTimeline event={templateData} eventData={eventData} colors={colors} />

      <CallToActionBanner event={templateData} colors={colors} onInscribeteClick={handleInscribeteClick} />

      {/* MODALES COMPETITORES Y TIEMPOS */}
      {showCompetitorsModal && <CompetitorsModal />}
      {showTimesModal && <TimesModal />}

      {/* 10. FOOTER */}
      <footer className="pc-footer">
        <div className="pc-footer-content">
          <p>© 2024 InnovateSport - Todos los derechos reservados</p>
        </div>
      </footer>

    </div>
  );
};

export default PCLayout;